import { Dispatch, FormEvent, SetStateAction, useState } from "react";
import { useNavigate } from "react-router-dom";

import { UserType } from "../../../../../../constants";
import { RadioButtonGroupData } from "../../../../../../models";
import { updateOrganisationUser } from "../../../../../../service/organisation";
import { ServiceError, Status } from "../../../../../../service/Shared";
import { useAuth } from "../../../../../../useAuth";
import { useIsLoadingWrapper } from "../../../../../../utils";
import { Toast } from "../../../../../../widget";

interface UseEditFormReturnData {
  radioValues: RadioButtonGroupData;
  role: string;
  active: boolean;
  errors: ServiceError[] | undefined;
  isHandleSubmitLoading: boolean;
  currentUserType: UserType;
  setRole: Dispatch<SetStateAction<string>>;
  setActive: Dispatch<SetStateAction<boolean>>;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
  handleCancel: () => void;
}

export const useEditForm = (
  userUuid: string | undefined,
  defaultRoleValue: string,
  defaultActiveValue: boolean,
  rowVersion: number,
  userSettingsRoute: string
): UseEditFormReturnData => {
  const navigate = useNavigate();
  const { currentOrganisationUuid, currentUserType } = useAuth();

  const [role, setRole] = useState(defaultRoleValue);
  const [active, setActive] = useState(defaultActiveValue);
  const [errors, setErrors] = useState<ServiceError[] | undefined>();
  const [actualRowVersion, setActualRowVersion] = useState(rowVersion);
  const [isHandleSubmitLoading, setIsHandleSubmitLoading] = useState(false);

  const radioValues = [
    { key: true, value: "Active" },
    { key: false, value: "Inactive" },
  ];

  const handleSubmit = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrors([]);

    if (userUuid && currentOrganisationUuid) {
      const res = await updateOrganisationUser({
        userUuid,
        organisationUuid: currentOrganisationUuid,
        role,
        active,
        rowVersion: actualRowVersion,
      });

      if (res.status === Status.Success && res.data) {
        Toast.success({ message: "User details changed successfully" });
        setActualRowVersion(res.data?.rowVersion);
      }

      if (res.status === Status.Error) {
        setErrors(res.errors);
      }
    }
  }, setIsHandleSubmitLoading);

  const handleCancel = (): void => {
    navigate(userSettingsRoute);
  };

  return {
    radioValues,
    role,
    active,
    errors,
    isHandleSubmitLoading,
    currentUserType,
    setRole,
    setActive,
    handleSubmit,
    handleCancel,
  };
};
